import { ApiRoutes } from '@gr/core/api-routes';
import type {
  CreatePostRequestDto,
  PostDto,
  PostFormatDto,
  PostListQuery,
  PostReadinessDto,
  PostStatusDto,
  PostSummaryDto,
  SavePostFormatRequestDto,
  SavePostStatusRequestDto,
  UpdatePostRequestDto,
} from '@gr/core/content/posts/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import type { HttpClient } from '../../../shared/http/http-client';

export class PostsApi {
  constructor(private readonly http: HttpClient) {}

  statuses(blogId: string): Promise<OperationResult<PostStatusDto[]>> {
    return this.http.get(ApiRoutes.posts.statuses(blogId));
  }
  createStatus(blogId: string, input: SavePostStatusRequestDto): Promise<OperationResult<PostStatusDto[]>> {
    return this.http.post(ApiRoutes.posts.statuses(blogId), input);
  }
  updateStatus(blogId: string, statusId: string, input: SavePostStatusRequestDto): Promise<OperationResult<PostStatusDto[]>> {
    return this.http.put(ApiRoutes.posts.status(blogId, statusId), input);
  }
  deleteStatus(blogId: string, statusId: string, replaceWith?: string): Promise<OperationResult<PostStatusDto[]>> {
    return this.http.delete(ApiRoutes.posts.status(blogId, statusId), { replaceWith });
  }
  reorderStatuses(blogId: string, statusIds: string[]): Promise<OperationResult<PostStatusDto[]>> {
    return this.http.put(ApiRoutes.posts.statusesOrder(blogId), { statusIds });
  }
  // POST-UC-10
  formats(blogId: string): Promise<OperationResult<PostFormatDto[]>> {
    return this.http.get(ApiRoutes.posts.formats(blogId));
  }
  createFormat(blogId: string, input: SavePostFormatRequestDto): Promise<OperationResult<PostFormatDto[]>> {
    return this.http.post(ApiRoutes.posts.formats(blogId), input);
  }
  updateFormat(blogId: string, formatId: string, input: SavePostFormatRequestDto): Promise<OperationResult<PostFormatDto[]>> {
    return this.http.put(ApiRoutes.posts.format(blogId, formatId), input);
  }
  deleteFormat(blogId: string, formatId: string): Promise<OperationResult<PostFormatDto[]>> {
    return this.http.delete(ApiRoutes.posts.format(blogId, formatId));
  }
  list(blogId: string, query: PostListQuery = {}, signal?: AbortSignal): Promise<OperationResult<PostSummaryDto[]>> {
    return this.http.get(ApiRoutes.posts.list(blogId), { ...query }, signal);
  }
  get(blogId: string, postId: string): Promise<OperationResult<PostDto>> {
    return this.http.get(ApiRoutes.posts.byId(blogId, postId));
  }
  create(blogId: string, input: CreatePostRequestDto): Promise<OperationResult<PostDto>> {
    return this.http.post(ApiRoutes.posts.list(blogId), input);
  }
  update(blogId: string, postId: string, input: UpdatePostRequestDto): Promise<OperationResult<PostDto>> {
    return this.http.patch(ApiRoutes.posts.byId(blogId, postId), input);
  }
  delete(blogId: string, postId: string): Promise<OperationResult<null>> {
    return this.http.delete(ApiRoutes.posts.byId(blogId, postId));
  }
  /** excludedMediaIds не передан — отметки оставшихся медиа сохраняются (POST-UC-14.CC-1). */
  setMedia(blogId: string, postId: string, mediaIds: string[], excludedMediaIds?: string[]): Promise<OperationResult<PostDto>> {
    return this.http.put(ApiRoutes.posts.media(blogId, postId), excludedMediaIds ? { mediaIds, excludedMediaIds } : { mediaIds });
  }
  setStatus(blogId: string, postId: string, statusId: string): Promise<OperationResult<PostDto>> {
    return this.http.put(ApiRoutes.posts.setStatus(blogId, postId), { statusId });
  }
  setVariant(blogId: string, postId: string, channelId: string, body: string): Promise<OperationResult<PostDto>> {
    return this.http.put(ApiRoutes.posts.variant(blogId, postId, channelId), { body });
  }
  readiness(blogId: string, postId: string): Promise<OperationResult<PostReadinessDto>> {
    return this.http.get(ApiRoutes.posts.readiness(blogId, postId));
  }
}
